import { Exchange, CombinedError } from 'urql';
import { pipe, tap } from 'wonka';
import { store } from '../app/store';
import { performUserLogout } from '../features/userLogin/userLoginSlice';
import { setCartItemsCount } from '../features/cart/cartSlice';

/**
 * Check if error is an Unauthorized error
 */
const isUnauthorizedError = (error: CombinedError): boolean => {
  // Check GraphQL errors
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors.some(
      (graphQLError) =>
        graphQLError.message === 'Unauthorized' ||
        graphQLError.extensions?.category === 'authorization'
    );
  }
  
  // Check network errors with 401 status
  if (error.networkError) {
    const networkError = error.networkError as any;
    if (networkError.statusCode === 401 || networkError.response?.status === 401) {
      return true;
    }
  }
  
  return false;
};

/**
 * Error exchange to handle Unauthorized errors globally
 * When an Unauthorized error is detected, it will:
 * 1. Logout the user
 * 2. Clear cart count
 * 3. Redirect to login page
 */
export const errorExchange: Exchange = ({ forward }) => {
  return (ops$) => {
    return pipe(
      ops$,
      forward,
      tap((result) => {
        // Check if result has an error
        if (result.error && isUnauthorizedError(result.error)) {
          // Dispatch logout action
          store.dispatch(performUserLogout(true));
          store.dispatch(setCartItemsCount(0));
          
          // Clear localStorage
          try {
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_id');
          } catch (e) {
            // Ignore errors when clearing localStorage
          }
          
          // Redirect to login page
          // Use window.location to force a full page reload and clear any state
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && currentPath !== '/forgot-password') {
            window.location.href = '/login';
          }
        }
      })
    );
  };
};

